import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Urls } from 'data';
import { getUserName } from 'helpers';
import { useAPI, useGetUser } from 'hooks';
import { PermissionsContext } from 'context';
import Locales from 'locales';
import {
  ChartColumnIncreasing,
  Users,
  Building2,
  Landmark,
  ShieldUser,
  LogOut,
  BanknoteArrowDown,
  Megaphone,
  PanelLeft,
  Menu,
  X,
  FileText,
  Key,
  FileCheck,
  Settings,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import logo from 'assets/img/sidebar/logo.svg';

const SidebarComponent = ({ onClose }) => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAgency, isAgencyPrimary, isBank, isAGLO, isSuperAdmin, isPrimaryUser, isMS, isDEI, isMAEC } =
    useGetUser();
  const { isPlatformLocked } = useContext(PermissionsContext);
  const { first_name: firstname, last_name: lastname, organization_name: organisationName } = user || {};
  const { api } = useAPI();

  const { data: preferences } = useQuery({
    queryFn: api.retrieve('preference', 'current/'),
    queryKey: ['preference/current/'],
  });

  const { data: organizations } = useQuery({
    queryFn: api.list('organization/'),
    queryKey: ['organization/'],
    enabled: !isAgency,
  });

  const organizationAcronym = organizations?.find((org) => org.name === organisationName)?.acronym;
  const { campaign_year: campaignYear } = preferences || {};

  const isActive = (itemPath) => location.pathname === itemPath;

  const userIcon = () => {
    if (isAgency) return <Building2 className="w-10 h-10 p-2 rounded-full bg-emerald-500 text-white" />;
    if (isBank) return <Landmark className="w-10 h-10 p-2 rounded-full bg-emerald-500 text-white" />;
    return <ShieldUser className="w-10 h-10 p-2 rounded-full bg-emerald-500 text-white" />;
  };

  const menuItems = () => {
    const items = [];

    if (isAgency || isAGLO || isMS || isDEI || isMAEC) {
      items.push({
        icon: Users,
        label: Locales.base.sidebar.trips,
        link: Urls.trip,
        active: isActive(Urls.trip),
      });
    }

    if (isAgency || isAGLO) {
      items.push({
        icon: Settings,
        label: Locales.base.sidebar.customizations,
        link: Urls.customization,
        active: isActive(Urls.customization),
      });
    }

    if (isAgency || isAGLO) {
      items.push({
        icon: BanknoteArrowDown,
        label: Locales.base.sidebar.deposits,
        link: Urls.deposit,
        active: isActive(Urls.deposit),
      });
    }

    if (isAgency || isAGLO || isBank) {
      items.push({
        icon: FileText,
        label: Locales.base.sidebar.invoices,
        link: Urls.invoice,
        active: isActive(Urls.invoice),
      });
    }

    if (isBank) {
      items.push({
        icon: Key,
        label: Locales.base.sidebar.api_key,
        link: Urls.apikey,
        active: isActive(Urls.apikey),
      });
    }

    if (isAgencyPrimary) {
      items.push({
        icon: FileCheck,
        label: Locales.base.sidebar.authorization,
        link: Urls.authorization,
        active: isActive(Urls.authorization),
      });
    }

    if (isAGLO) {
      items.push({
        icon: Building2,
        label: Locales.base.sidebar.agencies,
        link: Urls.agency,
        active: isActive(Urls.agency),
      });
    }

    if (isAGLO || isSuperAdmin) {
      items.push({
        icon: ChartColumnIncreasing,
        label: Locales.base.sidebar.dashboard,
        link: Urls.dashboard,
        active: isActive(Urls.dashboard),
      });
    }

    if (isSuperAdmin || isAGLO) {
      items.push({
        icon: Megaphone,
        label: Locales.base.sidebar.campaign,
        link: Urls.campaign,
        active: isActive(Urls.campaign),
      });
    }

    if (isPrimaryUser) {
      items.push({
        icon: ShieldUser,
        label: Locales.base.sidebar.users,
        link: Urls.user,
        active: isActive(Urls.user),
      });
    }

    return items;
  };

  const handleClose = () => {
    setIsMobileOpen(false);
    if (onClose) onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 27) {
      handleClose();
    }
  };

  const handleLogout = () => {
    navigate(Urls.front.logout);
  };

  return (
    <>
      {/* Header (mobile) */}
      <header className="fixed top-0 left-0 right-0 z-40 flex items-center justify-between bg-white border-b border-gray-200 px-4 py-3 lg:hidden">
        <button
          type="button"
          aria-label="Open menu"
          onClick={() => setIsMobileOpen(true)}
          className="p-2 rounded-md hover:bg-gray-100"
        >
          <Menu className="w-6 h-6 text-gray-700" />
        </button>
        <div className="flex items-center gap-2">
          <img src={logo} alt="eHadj" className="w-24 h-8 object-contain" />
          {campaignYear && <span className="text-sm text-gray-700">{campaignYear}</span>}
          {(isAgency || isAGLO) && isPlatformLocked && <i className="fa regular fa-lock text-gray-700" />}
        </div>
      </header>

      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className={cn('fixed inset-0 z-40 bg-black/50 lg:hidden', 'animate-in fade-in duration-300')}
          onClick={handleClose}
          role="button"
          tabIndex={0}
          aria-label="Fermer le menu"
          onKeyDown={handleKeyDown}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed top-0 left-0 z-50 h-screen bg-white border-r border-gray-200 flex flex-col',
          'transition-all duration-500 ease-in-out',
          collapsed ? 'lg:w-24' : 'lg:w-[240px]',
          isMobileOpen ? 'translate-x-0 w-full lg:w-[240px]' : 'translate-x-[-100%] lg:translate-x-0'
        )}
      >
        {/* Header */}
        <div
          className={cn(
            'p-6 border-b border-gray-200 relative',
            collapsed ? 'flex flex-col items-center' : 'flex items-center justify-between'
          )}
        >
          <div className="flex items-center gap-2">
            <img src={logo} alt="eHadj" className={cn(collapsed ? 'w-16 h-5' : 'w-28 h-9', 'object-contain')} />
            {!collapsed && campaignYear && <span className="text-sm text-gray-700">{campaignYear}</span>}
            {!collapsed && (isAgency || isAGLO) && isPlatformLocked && (
              <i className="fa regular fa-lock text-gray-700" />
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className="p-1 rounded-md hover:bg-gray-100 hidden md:inline-flex"
              onClick={() => setCollapsed((c) => !c)}
            >
              <PanelLeft className="w-6 h-6" />
            </button>

            <button
              type="button"
              aria-label="Fermer le menu"
              onClick={handleClose}
              className="absolute right-4 top-6 p-2 rounded-md hover:bg-gray-100 lg:hidden"
            >
              <X className="w-6 h-6 text-gray-700" />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {menuItems().map((item) => {
            const Icon = item.icon;
            return (
              <NavLink key={item.label} to={item.link} onClick={() => isMobileOpen && handleClose()}>
                <Button
                  variant={item.active ? 'secondary' : 'ghost'}
                  className={cn(
                    'w-full gap-3 text-sm font-medium transition-all duration-300',
                    collapsed ? 'justify-center' : 'justify-start',
                    item.active && 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                  )}
                >
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-full h-full" />
                  </span>
                  {!collapsed && <span>{item.label}</span>}
                </Button>
              </NavLink>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 space-y-3">
          <Button
            variant="ghost"
            onClick={handleLogout}
            className={cn(
              'w-full gap-3 text-sm text-red-600 hover:text-red-700 hover:bg-red-50',
              collapsed ? 'justify-center' : 'justify-start'
            )}
          >
            <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <LogOut className="w-full h-full" />
            </span>
            {!collapsed && <span>{Locales.base.sidebar.logout}</span>}
          </Button>

          <div className={cn('flex items-center gap-3 p-3 rounded-lg bg-gray-50', collapsed ? 'justify-center' : '')}>
            {userIcon()}
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-medium text-gray-900 truncate">{getUserName(firstname, lastname)}</p>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-700 ml-2">
                    {isAgency ? organisationName : organizationAcronym}
                  </span>
                </div>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

SidebarComponent.defaultProps = {
  onClose: null,
};

SidebarComponent.propTypes = {
  onClose: PropTypes.func,
};

export default SidebarComponent;
